/**
 * Bridge to access WorkOS getAccessToken outside React context.
 *
 * WorkOS AuthKit SDK manages tokens internally and provides getAccessToken()
 * via React hook. This singleton allows axios interceptors (non-React) to
 * access that function.
 *
 * Usage:
 * 1. EnrichAuthProvider registers getAccessToken during mount
 * 2. axios interceptor calls workosAuthBridge.getAccessToken()
 *
 * Note: Unlike JWT-based auth where tokens are stored in a Zustand store,
 * WorkOS handles token refresh internally. We only need to store the
 * function reference, not the token itself.
 */

type GetAccessTokenFn = () => Promise<string>;

class WorkOSAuthBridge {
  private static instance: WorkOSAuthBridge;
  private getAccessTokenFn: GetAccessTokenFn | null = null;

  private constructor() {}

  static getInstance(): WorkOSAuthBridge {
    if (!WorkOSAuthBridge.instance) {
      WorkOSAuthBridge.instance = new WorkOSAuthBridge();
    }
    return WorkOSAuthBridge.instance;
  }

  /**
   * Register the getAccessToken function from WorkOS AuthKit.
   * Should be called once during EnrichAuthProvider initialization.
   */
  register(fn: GetAccessTokenFn): void {
    this.getAccessTokenFn = fn;
  }

  /**
   * Get the current access token from WorkOS.
   * WorkOS SDK handles token refresh automatically.
   *
   * @throws Error if bridge is not initialized (EnrichAuthProvider not mounted)
   */
  async getAccessToken(): Promise<string> {
    if (!this.getAccessTokenFn) {
      throw new Error(
        'WorkOS auth bridge not initialized. Ensure EnrichAuthProvider is mounted before making API calls.',
      );
    }
    return this.getAccessTokenFn();
  }

  /**
   * Check if the bridge has been initialized with a getAccessToken function.
   */
  isInitialized(): boolean {
    return this.getAccessTokenFn !== null;
  }

  /**
   * Clear the registered function. Useful for testing or logout cleanup.
   */
  reset(): void {
    this.getAccessTokenFn = null;
  }
}

export const workosAuthBridge = WorkOSAuthBridge.getInstance();
